import { h, Fragment } from 'preact';
import Player from '@vimeo/player';
import { useEffect, useState, useRef, useLayoutEffect } from 'preact/hooks';
import clsx from 'clsx';
import PlayIcon from './icons/play.svg';
import PauseIcon from './icons/pause.svg';
import MuteIcon from './icons/mute.svg';
import UnmuteIcon from './icons/unmute.svg';
import { useWindowSize } from './window-size';
import useIsInViewport from './isInViewPort';
import useUniqueId from './useUniqueId';

const VimeoPlayer = ({
  url,
  autoplay,
  muted,
  loop = true,
  className,
  onPlayerInit,
  showControls = true,
  controlsPosition = 'bottom-right',
  controlsPositionMobile = 'bottom-right',
  forcePaused = false,
}) => {
  const videoId = useUniqueId();
  const videoRef = useRef(null);
  const { isTablet } = useWindowSize();
  const isInViewport = useIsInViewport(videoRef);
  const [userPaused, setUserPaused] = useState(false);

  const positionClasslist = isTablet
    ? `${controlsPositionMobile}-mobile`
    : `${controlsPosition}-desktop`;

  const leftSide = positionClasslist.indexOf('left') > -1;

  const bottomSide = positionClasslist.indexOf('bottom') > -1;

  autoplay = [true, 'true'].includes(autoplay);

  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [isMuted, setIsMuted] = useState(muted || autoplay);
  const [player, setPlayer] = useState(false);

  useLayoutEffect(() => {
    if (player) {
      if (isInViewport && !forcePaused && !isPlaying && !userPaused) {
        player.play();
        setIsPlaying(true);
      } else if (!isInViewport || (forcePaused && isPlaying)) {
        player.pause();
        setIsPlaying(false);
      }
    }
    // Not including isPlaying as a dependency on purpose.
    // If it were, this would immediately play the video any time it was paused.
  }, [forcePaused, player, isInViewport]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    ((player && player.destroy()) || Promise.resolve()).then(() => {
      const playerInst = new Player(videoId, {
        url,
        autoplay,
        muted: !!(autoplay || muted),
        loop,
        controls: false,
        playsinline: true,
        autopause: false,
        showControls,
      });
      setPlayer(playerInst);
      onPlayerInit?.(playerInst);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const handlePlay = (e) => {
    if (isPlaying) {
      e.preventDefault();
      player.pause();
      setIsPlaying(false);
      setUserPaused(true);
    } else {
      e.preventDefault();
      player.play();
      setIsPlaying(true);
      setUserPaused(false);
    }
  };

  const handleSound = (e) => {
    if (isMuted) {
      e.preventDefault();
      player.setVolume(1);
      setIsMuted(false);
    } else {
      e.preventDefault();
      player.setVolume(0);
      setIsMuted(true);
    }
  };

  return (
    <section ref={videoRef}>
      {
        <div className={`react-video-container relative ${className}`}>
          {showControls && (
            <>
              <button
                type="button"
                data-video-mute
                onClick={(e) => handleSound(e)}
                ariaLabel={isMuted ? 'Mute video' : 'Unmute video'}
                className={`block group ${
                  isTablet && bottomSide ? '-translate-y-[50px]' : ''
                }`}
              >
                <MuteIcon
                  className={`absolute w-10 h-10 z-1 cursor-pointer group-focus:ring-2 group-focus:ring-blue-500 ${
                    isMuted ? '' : 'hidden'
                  } ${positionClasslist}
                  ${
                    leftSide
                      ? 'laptop:translate-x-[50px]'
                      : 'laptop:-translate-x-[50px]'
                  }`}
                />
                <UnmuteIcon
                  className={`absolute w-10 h-10 z-1 cursor-pointer group-focus:ring-2 group-focus:ring-blue-500 ${
                    isMuted ? 'hidden' : ''
                  } ${positionClasslist}
                  ${
                    leftSide
                      ? 'laptop:translate-x-[50px]'
                      : 'laptop:-translate-x-[50px]'
                  }`}
                />
              </button>
              <button
                type="button"
                data-video-play
                onClick={(e) => handlePlay(e)}
                ariaLabel={isPlaying ? 'Pause video' : 'Play video'}
                className={clsx('block group', {
                  '-translate-y-[50px]': isTablet && bottomSide,
                })}
              >
                <PlayIcon
                  className={`absolute w-10 h-10 z-1 cursor-pointer translate-y-[50px] laptop:transform-none group-focus:ring-2 group-focus:ring-blue-500 ${
                    isPlaying ? 'hidden' : ''
                  } ${positionClasslist}`}
                />
                <PauseIcon
                  className={`absolute w-10 h-10 z-1 cursor-pointer translate-y-[50px] laptop:transform-none group-focus:ring-2 group-focus:ring-blue-500 ${
                    !isPlaying ? 'hidden' : ''
                  } ${positionClasslist}`}
                />
              </button>
            </>
          )}
          <div id={videoId} />
          {/* <div data-video-mute onClick={handleMute}>Mute</div> */}
        </div>
      }
    </section>
  );
};

export default VimeoPlayer;
